/**
 * surfaceSlots.ts — WHAT each product surface needs, and whether the Engine has it.
 *
 * THE PROBLEM THIS SOLVES, PRECISELY
 * ---------------------------------
 * The Engine publishes 253 capabilities on the integrated head, in 24 of its own
 * presentation groups. A user does not think in groups; they think "I need the
 * line list" or "I need to know what the duct fill is". So the workbench needs a
 * way to say "this surface is about those things" and then ask the manifest
 * whether it can serve them.
 *
 * The tempting answer — hard-code a per-surface list of capability ids — is
 * exactly the duplication this repository exists to remove, and it fails in a
 * specific way: a hard-coded list of what EXISTS goes stale the moment the Engine
 * changes, and a stale list that is missing an entry is indistinguishable from a
 * list that is correct. So the list here is a list of what this surface WANTS,
 * never a list of what the Engine HAS. Every slot is resolved against the live
 * manifest at render time, and a slot the manifest does not publish becomes an
 * explicit, NAMED absence.
 *
 * WHY NAMING AN ABSENCE IS THE POINT
 * ----------------------------------
 * A surface that silently omits the line list looks exactly like a surface where
 * the line list is empty. Those are different facts and a user cannot tell them
 * apart, so this module's contract is: every slot is either served by a
 * descriptor the Engine published, or displayed by name as not published. There
 * is no third state, and no path here that can hide a slot.
 *
 * WHAT A SLOT IS NOT
 * ------------------
 * A slot names an Engine capability identity and a human purpose. It holds NO
 * schema, NO field name, NO enumeration, NO unit, NO rule, and NO expected
 * value. Everything a slot actually shows on screen comes from the descriptor the
 * Engine published for it, read through `capabilityManifest.ts` and
 * `capabilityAction.ts`. If a slot could describe the shape of its own answer,
 * this file would be a second source of truth wearing a product vocabulary, and
 * `slotHoldsNoSemantics` in the test suite exists to keep that from happening.
 *
 * WHY THIS IS NOT A FILTER
 * ------------------------
 * `automationAbsence.test.ts` requires the absence of automation to hold by
 * absence from the manifest rather than by a denylist. A requirement list is not
 * a denylist and is not positioned to become one: it cannot exclude anything,
 * because every capability the Engine publishes remains listed and runnable in
 * the generic catalogue regardless of whether any slot names it. That property is
 * asserted directly — see `everyPublishedCapabilityStaysReachable`.
 */

import {
  allDescriptors,
  findCapability,
  type CapabilityDescriptor,
  type CapabilityManifest,
} from "../../api/capabilityManifest";
import { inspect, type ActionResolution } from "../../api/capabilityAction";

/**
 * How a surface uses a capability.
 *
 * `read` is a query the user runs to see a fact. `write` is a reviewed mutation
 * that accumulates into a change set. The distinction is a display one — it
 * decides which of the two controls the slot offers — and it is checked against
 * the descriptor's own `kind` rather than asserted, so a slot declared `write`
 * that the Engine published as a query is reported rather than rendered with a
 * button the Engine would refuse.
 */
export type SlotRole = "read" | "write";

/** One thing a surface needs from the Engine. */
export interface SurfaceSlot {
  /** Stable within this repository; never shown, never sent. */
  readonly key: string;
  /** The Engine capability identity this slot wants. Reported when absent. */
  readonly capabilityId: string;
  /**
   * What a person needs this for, in one line.
   *
   * A statement of PURPOSE, written by whoever asked for the surface. It is not a
   * specification of the answer: it does not say what fields come back, what the
   * values may be, or what a good answer looks like. The Engine's descriptor says
   * all of that, and only the Engine.
   */
  readonly purpose: string;
  readonly role: SlotRole;
}

/** One product surface: a named set of slots. */
export interface SurfaceDefinition {
  readonly key: string;
  readonly title: string;
  /** Why the surface exists, in one line. Display only. */
  readonly purpose: string;
  readonly slots: readonly SurfaceSlot[];
}

// ---------------------------------------------------------------------------
// The surfaces
// ---------------------------------------------------------------------------
//
// Read this table as a PRODUCT REQUIREMENT LIST, in that order of authority.
// It says what a discipline view is for. It does not say what the Engine
// supports, and `missingOnManifest` computes that from the manifest at run time
// so the two can never be confused.

/**
 * THE SURFACES, and the honest reason each one is shaped the way it is.
 *
 * Where a slot names a capability the integrated Engine does not publish, that
 * is a FINDING, not a design choice: the surface says so by name instead of
 * quietly rendering less. Those cases are enumerated in
 * `tests/surfaceAvailability.test.ts` against the real manifest, so an Engine that
 * starts publishing one turns that suite green on the next refresh and the
 * product gains the capability with no source edit here — which is the property
 * the whole design is for.
 */
export const SURFACES: readonly SurfaceDefinition[] = Object.freeze<SurfaceDefinition[]>([
  {
    key: "process",
    title: "P&ID and process",
    purpose: "Lines, their specification, their conditions, and the registers that describe them.",
    slots: Object.freeze([
      { key: "line-list", capabilityId: "query/process.streams", purpose: "The line list: what the process connects.", role: "read" },
      { key: "line-spec", capabilityId: "operation/line-spec/create", purpose: "A line specification, created and revised under review.", role: "write" },
      { key: "line-spec-revise", capabilityId: "operation/line-spec/revise", purpose: "A revision of an existing line specification.", role: "write" },
      { key: "design-conditions", capabilityId: "operation/process/case/upsert", purpose: "The design case a process stream is sized against.", role: "write" },
      { key: "operating-modes", capabilityId: "operation/process/mode/upsert", purpose: "The operating modes a case is evaluated in.", role: "write" },
      { key: "operating-conditions", capabilityId: "query/process.configurations", purpose: "Which configurations exist and how they differ.", role: "read" },
      { key: "sizing", capabilityId: "query/process.sizing", purpose: "The Engine's own sizing answer, with its inputs named.", role: "read" },
      { key: "balance", capabilityId: "query/process.balance", purpose: "The material balance as the Engine computed it.", role: "read" },
      { key: "connections", capabilityId: "query/process.connections", purpose: "Which units and streams are connected to what.", role: "read" },
      { key: "openings", capabilityId: "operation/process/port/upsert", purpose: "An opening on a unit, created and retired under review.", role: "write" },
      { key: "materials", capabilityId: "operation/process/stream/upsert", purpose: "A process stream and the material it carries.", role: "write" },
      { key: "method-basis", capabilityId: "operation/process/batch-basis/upsert", purpose: "The basis a batch calculation is made on.", role: "write" },
      { key: "off-page-continuation", capabilityId: "operation/diagram/symbol/add", purpose: "A symbol carrying an off-page reference to another sheet.", role: "write" },
      { key: "dependencies", capabilityId: "query/process.dependencies", purpose: "What a process quantity depends on.", role: "read" },
      { key: "quantity-profiles", capabilityId: "query/process.quantity-profiles", purpose: "The declared quantity profile behind a stream value.", role: "read" },
      { key: "diagnostics-consistency", capabilityId: "query/consistency.compare", purpose: "Whether the model is internally consistent.", role: "read" },
      { key: "diagnostics-coverage", capabilityId: "query/coverage.audit", purpose: "What the model does not yet describe.", role: "read" },
      { key: "reviewed-plans", capabilityId: "operation/process/recompute", purpose: "A reviewed recomputation of the process model.", role: "write" },
    ]),
  },
  {
    key: "electrical",
    title: "Electrical and instrumentation",
    purpose: "Four different facts about a circuit, kept visibly apart, plus the Engine's calculation basis.",
    slots: Object.freeze([
      { key: "logical-signal", capabilityId: "query/control.trace", purpose: "The logical signal: what a control loop connects.", role: "read" },
      { key: "electrical-supply", capabilityId: "query/electrical.supplies", purpose: "The electrical supply: which conductor energises a device.", role: "read" },
      { key: "schematic-route", capabilityId: "operation/diagram/trace/add", purpose: "The schematic route: a connection drawn on a sheet.", role: "write" },
      { key: "physical-route", capabilityId: "query/electrical.route-calculations", purpose: "The physical route: conductor length, duct fill and voltage drop, as the Engine computed them.", role: "read" },
      { key: "devices", capabilityId: "query/entity.lookup", purpose: "The devices the project contains.", role: "read" },
      { key: "cables-cores-terminals", capabilityId: "query/electrical.cables", purpose: "Cables, cores and terminations, and the schedule over them.", role: "read" },
      { key: "io-channels", capabilityId: "query/electrical.io", purpose: "Input and output channels and their assignments.", role: "read" },
      { key: "networks", capabilityId: "query/electrical.networks", purpose: "The network segments devices sit on.", role: "read" },
      { key: "calculation-declaration", capabilityId: "operation/electrical/calculation-declaration/create", purpose: "A durable, reviewed declaration of what a calculation is based on.", role: "write" },
      { key: "calculation-review", capabilityId: "operation/electrical/calculation-declaration/review", purpose: "A reviewed decision on a declared calculation.", role: "write" },
      { key: "physical-bom", capabilityId: "query/electrical.route-calculations", purpose: "The procurement quantities the Engine derived, with their provenance.", role: "read" },
    ]),
  },
  {
    key: "panel",
    title: "Physical panel",
    purpose: "The canonical panel in millimetre frames: surfaces, rails, ducts, zones, placements and routes.",
    slots: Object.freeze([
      { key: "assembly", capabilityId: "operation/panel/assembly/create", purpose: "A panel assembly, in its own millimetre frame.", role: "write" },
      { key: "assembly-update", capabilityId: "operation/panel/assembly/update", purpose: "A change to an existing assembly.", role: "write" },
      { key: "surfaces", capabilityId: "operation/panel/surface/create", purpose: "A mounting surface, in world millimetres.", role: "write" },
      { key: "rails", capabilityId: "operation/panel/rail/create", purpose: "A rail, and the devices that mount to it.", role: "write" },
      { key: "ducts", capabilityId: "operation/panel/duct/create", purpose: "A duct, and the segregation it enforces.", role: "write" },
      { key: "zones", capabilityId: "operation/panel/zone/create", purpose: "A clearance or segregation zone.", role: "write" },
      { key: "footprints", capabilityId: "operation/panel/footprint/adopt", purpose: "The exact adopted footprint version a placement rests on.", role: "write" },
      { key: "placement", capabilityId: "operation/panel/mount/place", purpose: "A placement, positioned by the Engine rather than by this browser.", role: "write" },
      { key: "move", capabilityId: "operation/panel/mount/move", purpose: "A move, resolved by the Engine before it is applied.", role: "write" },
      { key: "rotate", capabilityId: "operation/panel/mount/rotate", purpose: "A rotation, resolved by the Engine before it is applied.", role: "write" },
      { key: "remove", capabilityId: "operation/panel/mount/remove", purpose: "Removing a placement.", role: "write" },
      { key: "placement-lock", capabilityId: "operation/panel/mount/lock", purpose: "Taking a reviewed lock on a placement.", role: "write" },
      { key: "placement-unlock", capabilityId: "operation/panel/mount/unlock", purpose: "Releasing a placement lock, with its reason.", role: "write" },
      { key: "layout-reconcile", capabilityId: "operation/panel/layout/reconcile", purpose: "A deterministic layout proposal, reconciled against base and current.", role: "write" },
      { key: "routes", capabilityId: "operation/panel/route/create", purpose: "The physical route network between two semantic endpoints.", role: "write" },
      { key: "route-segments", capabilityId: "operation/panel/route/segment/add", purpose: "A segment of a physical route.", role: "write" },
      { key: "route-lock", capabilityId: "operation/panel/route/lock", purpose: "A reviewed lock on a route, or its release.", role: "write" },
      { key: "route-review", capabilityId: "operation/panel/route/review", purpose: "A reviewed decision on a proposed route.", role: "write" },
      { key: "route-length", capabilityId: "query/electrical.route-calculations", purpose: "Physical routed length, exactly as the Engine derived it.", role: "read" },
      { key: "duct-fill", capabilityId: "query/electrical.route-calculations", purpose: "Duct fill and segregation findings, with their calculation provenance.", role: "read" },
      { key: "voltage-drop", capabilityId: "query/electrical.route-calculations", purpose: "Voltage drop and capacity, or the Engine's explicit statement that it is unresolved.", role: "read" },
      { key: "fit-diagnostics", capabilityId: "operation/panel/layout/reconcile", purpose: "Infeasible fits, collisions, incompatible mounts and clearance violations.", role: "write" },
      { key: "route-staleness", capabilityId: "operation/panel/route/review", purpose: "Whether a route is stale against the state it was proposed from.", role: "write" },
    ]),
  },
  {
    key: "atex",
    title: "Hazardous areas",
    purpose: "What is known about a zone, what is not, and on what basis — never more than the Engine asserts.",
    slots: Object.freeze([
      { key: "determination", capabilityId: "operation/atex/determination-upsert", purpose: "The assessed determination for a zone, and the basis it rests on.", role: "write" },
      { key: "zone-extent", capabilityId: "query/atex.zone", purpose: "The zone's spatial extent, as the Engine holds it.", role: "read" },
      { key: "physical-extent", capabilityId: "query/atex.physical-extent", purpose: "The physical layout the zone classification applies to.", role: "read" },
      { key: "schematic-extent", capabilityId: "query/atex.schematic-extent", purpose: "The schematic footprint, which is not proof of physical extent.", role: "read" },
      { key: "evidence", capabilityId: "query/atex.evidence", purpose: "The evidence a classification rests on.", role: "read" },
      { key: "applicability", capabilityId: "query/atex.applicability", purpose: "Which standard, edition and method the classification claims.", role: "read" },
      { key: "legacy-migration", capabilityId: "operation/atex/legacy-migrate", purpose: "Migrating a legacy zone without inferring an assessment.", role: "write" },
    ]),
  },
  {
    key: "safety",
    title: "Safety and lifecycle",
    purpose: "HAZOP, safeguards, safety functions, proof tests and the lifecycle gates that follow.",
    slots: Object.freeze([
      { key: "hazop-chain", capabilityId: "operation/safety/hazop/chain/create", purpose: "A HAZOP chain, from node through deviation to action.", role: "write" },
      { key: "hazop-link", capabilityId: "operation/safety/hazop/chain/link", purpose: "Linking a safeguard or an action into a chain.", role: "write" },
      { key: "hazop-act", capabilityId: "operation/safety/hazop/chain/act", purpose: "Acting on a chain, and recording who decided what.", role: "write" },
      { key: "consequence", capabilityId: "operation/safety/hazop/chain/create", purpose: "The consequence a deviation produces.", role: "write" },
      { key: "safeguard", capabilityId: "operation/safety/hazop/chain/link", purpose: "A safeguard credited against a consequence.", role: "write" },
      { key: "action", capabilityId: "operation/safety/hazop/chain/act", purpose: "An action, and whether it has been taken.", role: "write" },
      { key: "safety-function", capabilityId: "operation/safety/function/create", purpose: "A safety function, and the revisions it has been through.", role: "write" },
      { key: "safety-function-review", capabilityId: "operation/safety/function/review", purpose: "A reviewed decision on a safety function.", role: "write" },
      { key: "safety-specification", capabilityId: "operation/safety/spec/create", purpose: "The specification a safety function is verified against.", role: "write" },
      { key: "proof-test-plan", capabilityId: "operation/safety/proof-test/create", purpose: "A proof-test plan.", role: "write" },
      { key: "proof-test-result", capabilityId: "operation/safety/proof-test/result/append", purpose: "A proof-test result, appended to the plan it satisfies.", role: "write" },
      { key: "proof-test-staleness", capabilityId: "operation/safety/proof-test/staleness/record", purpose: "Recording that a test interval lapsed against a changed input.", role: "write" },
      { key: "design-intent", capabilityId: "operation/safety/assertion/design-intent/record", purpose: "The design intent an engineering assertion rests on.", role: "write" },
      { key: "measurement", capabilityId: "operation/safety/assertion/measurement/record", purpose: "A measurement, which does not overwrite design intent.", role: "write" },
      { key: "phase", capabilityId: "operation/safety/lifecycle/phase/create", purpose: "A lifecycle phase, and its acceptance criteria.", role: "write" },
      { key: "phase-entry", capabilityId: "operation/safety/lifecycle/phase/enter", purpose: "Entering a phase, which requires its actual evidence.", role: "write" },
      { key: "phase-acceptance", capabilityId: "operation/safety/lifecycle/phase/acceptance", purpose: "An acceptance decision on a phase, with its actor retained.", role: "write" },
      { key: "activity", capabilityId: "operation/safety/lifecycle/activity/record", purpose: "Work performed in a phase, distinct from the phase itself.", role: "write" },
      { key: "object-state", capabilityId: "operation/safety/lifecycle/object-state/record", purpose: "The state a safety-relevant object is in.", role: "write" },
      { key: "fat-sat", capabilityId: "operation/safety/lifecycle/phase/exit-evidence", purpose: "Factory and site acceptance evidence for a phase exit.", role: "write" },
      { key: "loop-check", capabilityId: "operation/safety/lifecycle/activity/record", purpose: "A loop check, as a recorded activity with its own evidence.", role: "write" },
      { key: "commissioning", capabilityId: "operation/safety/lifecycle/phase/enter", purpose: "Commissioning, as a phase with acceptance criteria.", role: "write" },
      { key: "moc", capabilityId: "operation/safety/lifecycle/object-state/record", purpose: "A management-of-change record, and what it retired.", role: "write" },
      { key: "lifecycle-readback", capabilityId: "query/safety.lifecycle", purpose: "One safety or lifecycle record, read back with its decisions and unknowns.", role: "read" },
      { key: "lifecycle-readiness", capabilityId: "query/lifecycle.readiness", purpose: "Which lifecycle gates are open and what blocks them.", role: "read" },
    ]),
  },
  {
    key: "rationale",
    title: "Studies and rationale",
    purpose: "Why a decision was made, retrieved fresh — never from this browser's memory.",
    slots: Object.freeze([
      { key: "selected-alternative", capabilityId: "query/scenario.explain", purpose: "The alternative that was selected, and why.", role: "read" },
      { key: "rejected-alternatives", capabilityId: "query/scenario.compare", purpose: "The alternatives that were rejected, and on what stated constraint.", role: "read" },
      { key: "selection-basis", capabilityId: "query/scenario.explain", purpose: "The basis the selection was made on.", role: "read" },
      { key: "open-assumptions", capabilityId: "query/scenario.run", purpose: "The assumptions a study is still resting on.", role: "read" },
      { key: "reopening-triggers", capabilityId: "query/scenario.report", purpose: "What would reopen the decision.", role: "read" },
      { key: "record-state", capabilityId: "query/scenario.status", purpose: "Whether the study is a draft or has been published.", role: "read" },
      { key: "definitions", capabilityId: "query/scenario.definitions", purpose: "The study's declared constraints, options and schedules.", role: "read" },
    ]),
  },
]);

// ---------------------------------------------------------------------------
// Resolution against the live manifest
// ---------------------------------------------------------------------------

/** A slot the manifest publishes, and the resolution the loop derived for it. */
export interface ServedSlot {
  readonly slot: SurfaceSlot;
  readonly descriptor: CapabilityDescriptor;
  /** Undefined when the Engine publishes the capability but this browser cannot address it. */
  readonly resolution: ActionResolution;
  /**
   * Whether the slot's declared role agrees with what the Engine published.
   *
   * A slot declared `read` whose descriptor is an operation is a mismatch, and
   * the UI says so rather than offering the wrong control. This is checked
   * against `descriptor.kind`, which is the Engine's own classification.
   */
  readonly roleAgrees: boolean;
}

/** A slot the manifest does not publish. Named, never hidden. */
export interface AbsentSlot {
  readonly slot: SurfaceSlot;
  /** The same missing-publication language the transport reader uses. */
  readonly missing: string;
  readonly detail: string;
}

export interface ResolvedSurface {
  readonly definition: SurfaceDefinition;
  readonly served: readonly ServedSlot[];
  readonly absent: readonly AbsentSlot[];
}

/**
 * Resolve every slot of a surface against the manifest the Engine published.
 *
 * Pure, and total: every slot lands in exactly one of `served` or `absent`. That
 * is the property the UI relies on to be able to claim it has not hidden
 * anything, and it is asserted per slot rather than per surface so a single
 * omission cannot hide inside a passing total.
 */
export function resolveSurface(manifest: CapabilityManifest, definition: SurfaceDefinition): ResolvedSurface {
  const served: ServedSlot[] = [];
  const absent: AbsentSlot[] = [];
  for (const slot of definition.slots) {
    const descriptor = findCapability(manifest, slot.capabilityId);
    if (descriptor === undefined) {
      absent.push({
        slot,
        missing: `a capability published as "${slot.capabilityId}"`,
        detail:
          `The Engine's manifest does not publish ${slot.capabilityId}, so this surface cannot show `
          + `${slot.purpose.charAt(0).toLowerCase()}${slot.purpose.slice(1)}. That is a fact about the `
          + "manifest, not an empty result: the capability is not absent from the project, it is absent from "
          + "the contract this workbench was given. Nothing here is inferred in its place.",
      });
      continue;
    }
    served.push({
      slot,
      descriptor,
      resolution: inspect(manifest, descriptor),
      roleAgrees: slot.role === "write" ? descriptor.kind !== "query" : descriptor.kind === "query",
    });
  }
  return { definition, served, absent };
}

/** Resolve every surface this repository defines. */
export function resolveAllSurfaces(manifest: CapabilityManifest): readonly ResolvedSurface[] {
  return SURFACES.map(definition => resolveSurface(manifest, definition));
}

export function surfaceByKey(key: string): SurfaceDefinition | undefined {
  return SURFACES.find(definition => definition.key === key);
}

/**
 * Every capability the Engine publishes, whether or not a slot names it.
 *
 * Exists so the workbench can PROVE the slot table is a requirement list rather
 * than a filter. `everyPublishedCapabilityStaysReachable` compares this against
 * the union of the slot table: any capability the table happens to name is still
 * here, and the generic catalogue still lists all of them, so the table cannot
 * become the place where availability is decided.
 */
export function publishedCapabilityIds(manifest: CapabilityManifest): readonly string[] {
  return allDescriptors(manifest).map(descriptor => descriptor.id).sort();
}
